import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, Barbell, PersonSimpleRun, Bicycle, SwimmingPool } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const ACTIVITY_TYPES = [
  { key: 'caminhada', label: 'Caminhada', icon: PersonSimpleRun },
  { key: 'corrida', label: 'Corrida', icon: PersonSimpleRun },
  { key: 'musculacao', label: 'Musculação', icon: Barbell },
  { key: 'natacao', label: 'Natação', icon: SwimmingPool },
  { key: 'bicicleta', label: 'Bicicleta', icon: Bicycle },
  { key: 'yoga', label: 'Yoga/Pilates', icon: PersonSimpleRun },
  { key: 'outro', label: 'Outro', icon: PersonSimpleRun },
];

export default function AtividadePage() {
  const confirmDelete = useConfirmDelete();
  const [activities, setActivities] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ type: 'caminhada', duration_min: 30, date: new Date().toISOString().split('T')[0], notes: '' });

  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/my-activities`); setActivities(data); } catch {}
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    if (!form.type || !form.duration_min) { toast.error('Preenche o tipo e a duração'); return; }
    try { await axios.post(`${API}/api/my-activities`, form); toast.success('Atividade registada'); setShowAdd(false); fetch(); } catch { toast.error('Erro'); }
  };
  const del = async (id) => {
    const a = activities.find(x => x.id === id);
    const typeInfo = ACTIVITY_TYPES.find(t => t.key === a?.type);
    const ok = await confirmDelete({ description: `Vai apagar a atividade "${typeInfo?.label || a?.type || ''}" de ${a?.date || ''}.` });
    if (!ok) return;
    await axios.delete(`${API}/api/my-activities/${id}`); toast.success('Eliminada'); fetch();
  };

  // Stats
  const thisWeek = activities.filter(a => {
    const d = new Date(a.date); const now = new Date(); const diff = (now - d) / 86400000;
    return diff >= 0 && diff < 7;
  });
  const totalMin = thisWeek.reduce((s, a) => s + (a.duration_min || 0), 0);

  return (
    <div className="fade-in" data-testid="atividade-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Atividade Física</h2>
          <p className="text-xs text-brand-muted mt-0.5">Regista o teu exercício</p>
        </div>
        <button onClick={() => { setForm({ type: 'caminhada', duration_min: 30, date: new Date().toISOString().split('T')[0], notes: '' }); setShowAdd(true); }} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="add-activity-btn">
          <Plus size={16} /> Registar
        </button>
      </div>

      {/* Weekly summary */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5 text-center">
        <p className="text-xs text-brand-muted uppercase tracking-wider mb-1">Esta semana</p>
        <p className="text-3xl font-heading font-bold text-brand-green">{totalMin}</p>
        <p className="text-xs text-brand-muted">minutos de atividade ({thisWeek.length} sessões)</p>
      </div>

      {/* Activity list */}
      {activities.length === 0 ? (
        <div className="text-center py-12">
          <Barbell size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted text-sm">Sem atividades registadas.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {activities.map(a => {
            const typeInfo = ACTIVITY_TYPES.find(t => t.key === a.type) || ACTIVITY_TYPES[6];
            const Icon = typeInfo.icon;
            return (
              <div key={a.id} className="bg-white border border-brand-border rounded-2xl p-3 flex items-center gap-3" data-testid={`activity-${a.id}`}>
                <div className="w-10 h-10 rounded-xl bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} className="text-brand-green" weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-brand-text">{typeInfo.label}</p>
                  <p className="text-[10px] text-brand-muted">{fmtDatePt(a.date)} · {a.duration_min} min{a.notes ? ` · ${a.notes}` : ''}</p>
                </div>
                <button onClick={() => del(a.id)} className="p-1 text-brand-placeholder hover:text-red-500" data-testid={`delete-activity-${a.id}`}><Trash size={14} /></button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Registar atividade</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Tipo de atividade</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {ACTIVITY_TYPES.map(t => (
                  <button key={t.key} type="button" onClick={() => setForm({ ...form, type: t.key })}
                    className={`flex flex-col items-center gap-1 p-2 rounded-xl text-[10px] font-medium ${form.type === t.key ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>
                    <t.icon size={16} /> {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-brand-muted mb-1 block">Duração (min)</Label><Input type="number" value={form.duration_min} onChange={e => setForm({ ...form, duration_min: parseInt(e.target.value) || 0 })} className="rounded-xl border-brand-border" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" /></div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas (opcional)</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Ex: Corrida no parque" className="rounded-xl border-brand-border" /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-activity-btn">Registar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
